import { SendOutlined } from '@ant-design/icons'
import { useMutation } from '@apollo/client/react'
import { Button } from 'antd'
import { useState } from 'react'
import {
  DeleteDocumentDocument,
  type DocumentFragment,
  DocumentStage,
  UpdateDocumentStageDocument,
} from '~app/graph/generated/graphql'
import TransferAllDialog from '../../components/Elements/TransferAllDialog'

/**
 * Actions component
 */

const styles: InlineStyles = {
  actions: {
    position: 'fixed',
    bottom: 30,
    right: 30,
  },
  actionButton: {
    marginRight: 20,
    width: 56,
    height: 56,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}

interface Props {
  tagger: string
  documents: DocumentFragment[]
  refetchDocuments: () => Promise<any>
}

const HomeActions = ({ tagger, documents, refetchDocuments }: Props) => {
  const [dialogsOpen, setDialogsOpen] = useState({
    transferAll: false,
    deleteAll: false,
  })

  /* For debugging purposes, we are ensuring here that there are no duplicate ids */
  const documentIds = Array.from(
    new Set(
      documents.map(document => ({
        documentId: document.id,
        customerId: document.customer?.id,
      }))
    )
  )
  const hasOpenDocuments = documents.some(
    document => document.stage === DocumentStage.ManualProcessingStarted
  )

  const hasTagger = !!tagger

  // Mutations
  const [updateDocumentStage] = useMutation(UpdateDocumentStageDocument)
  const [deleteDocument] = useMutation(DeleteDocumentDocument)

  /**
   * "transfer all" action triggers multiple mutations in a promise,
   * so we track loading state using this separate flag.
   * This allows us to disable submit while the request is loading
   */
  const [transferAllLoading, setTransferAllLoading] = useState(false)

  const onTransferAll = async () => {
    setTransferAllLoading(true)
    try {
      // execute mutation for each document, then refetch documents list
      await Promise.all(
        documentIds.map(({ documentId, customerId }) => {
          if (!customerId) {
            return deleteDocument({
              variables: {
                id: documentId,
              },
            })
          }
          return updateDocumentStage({
            variables: {
              id: documentId,
              stage: DocumentStage.Validated,
            },
          })
        })
      )
      await refetchDocuments()

      // close dialog
      setDialogsOpen({ ...dialogsOpen, transferAll: false })
    } catch (error) {
      console.error(error)
    } finally {
      setTransferAllLoading(false)
    }
  }

  return (
    <div style={styles.actions}>
      <Button
        type="primary"
        shape="circle"
        size="large"
        icon={<SendOutlined style={{ fontSize: 24 }} />}
        style={styles.actionButton}
        disabled={!hasOpenDocuments}
        onClick={() => setDialogsOpen({ ...dialogsOpen, transferAll: true })}
      />
      <TransferAllDialog
        open={dialogsOpen.transferAll}
        isDocumentListFiltered={hasTagger}
        loading={transferAllLoading}
        onTransfer={onTransferAll}
        onCloseDialog={() =>
          setDialogsOpen({ ...dialogsOpen, transferAll: false })
        }
      />
    </div>
  )
}

export default HomeActions
